import { createContext, useCallback, useContext, useEffect, useState } from "react";

// Lightweight, dependency-free i18n. English is the key, so any un-translated
// string simply falls back to English — never a crash, never a blank label.
// Urdu is written in the Arabic script; we keep the layout LTR (numbers, dates,
// currency and product names all read left-to-right) and only swap the words.

const STORAGE_KEY = "hft_lang";
export const LANGS = ["en", "ur"];

export const MONTHS_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
export const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// Urdu translations, keyed by the exact English string. `{name}`-style tokens
// are filled in by t(string, vars).
const UR = {
  // Header / auth
  "Sign out": "لاگ آؤٹ",
  "Admin sign in": "ایڈمن سائن ان",
  "Enter your credentials to open the ledger.": "لیجر کھولنے کے لیے اپنی تفصیلات درج کریں۔",
  "Email address": "ای میل ایڈریس",
  "Password": "پاس ورڈ",
  "Sign in": "سائن ان",
  "Signing in…": "سائن ان ہو رہا ہے…",
  "Your wholesale fodder trade, tallied to the last dirham.": "آپ کا ہول سیل چارے کا کاروبار، آخری درہم تک حساب کے ساتھ۔",
  "Log every deal, track purchases and sales per item, and watch your profit add up — automatically.": "ہر سودہ درج کریں، ہر آئٹم کی خرید و فروخت پر نظر رکھیں، اور اپنے منافع کو خود بخود بڑھتا دیکھیں۔",

  // Dashboard
  "Welcome to Habib Fodders Trading LLC 👋": "حبیب فوڈرز ٹریڈنگ ایل ایل سی میں خوش آمدید 👋",
  "Your wholesale fodder trade — tracked to the last dirham.": "آپ کا ہول سیل چارے کا کاروبار — آخری درہم تک حساب کے ساتھ۔",
  "Products": "پروڈکٹس",
  "Open a product to see its monthly logs.": "ماہانہ ریکارڈ دیکھنے کے لیے کوئی پروڈکٹ کھولیں۔",
  "Add product": "پروڈکٹ شامل کریں",
  "+ Add product": "+ پروڈکٹ شامل کریں",
  "No products yet — add your first one to start logging deals.": "ابھی کوئی پروڈکٹ نہیں — سودے درج کرنا شروع کرنے کے لیے اپنی پہلی پروڈکٹ شامل کریں۔",
  "{n} month": "{n} مہینہ",
  "{n} months": "{n} مہینے",
  "Lists": "فہرستیں",
  "Add, edit, or remove item types, customers, and suppliers so they're ready to pick in the deal dropdowns.": "آئٹم کی اقسام، گاہک اور سپلائرز شامل، ترمیم یا حذف کریں تاکہ وہ سودے کے ڈراپ ڈاؤن میں منتخب کرنے کے لیے تیار ہوں۔",
  "Add, edit, or remove product types, item types, customers, and suppliers so they're ready to pick in the product and deal dropdowns.": "پروڈکٹ کی اقسام، آئٹم کی اقسام، گاہک اور سپلائرز شامل، ترمیم یا حذف کریں تاکہ وہ پروڈکٹ اور سودے کے ڈراپ ڈاؤن میں منتخب کرنے کے لیے تیار ہوں۔",
  "Nothing added yet.": "ابھی کچھ شامل نہیں کیا گیا۔",
  "View more": "مزید دیکھیں",

  // Lookup card headings / list titles
  "Item Types": "آئٹم کی اقسام",
  "Customers": "گاہک",
  "Suppliers": "سپلائرز",
  "Product Types": "پروڈکٹ کی اقسام",
  "All Products": "تمام پروڈکٹس",
  "List": "فہرست",

  // Product page
  "Rename": "نام بدلیں",
  "Edit product": "پروڈکٹ میں ترمیم",
  "Update this product's name and type.": "اس پروڈکٹ کا نام اور قسم اپ ڈیٹ کریں۔",
  "Classify products by type so their item type dropdown only shows matching values.": "پروڈکٹس کو قسم کے مطابق درجہ بندی کریں تاکہ ان کا آئٹم کی قسم ڈراپ ڈاؤن صرف ملتی جلتی اقدار دکھائے۔",
  "Delete": "حذف کریں",
  "All products": "تمام پروڈکٹس",
  "Rename product": "پروڈکٹ کا نام بدلیں",
  "Delete product": "پروڈکٹ حذف کریں",
  "Delete “{name}” and all of its deals? This can’t be undone.": "کیا “{name}” اور اس کے تمام سودے حذف کر دیے جائیں؟ یہ عمل واپس نہیں ہو سکتا۔",
  "Delete \"{name}\" and all of its deals? This can't be undone.": "کیا \"{name}\" اور اس کے تمام سودے حذف کر دیے جائیں؟ یہ عمل واپس نہیں ہو سکتا۔",
  "Monthly logs": "ماہانہ ریکارڈ",
  "Tap any month below to view its deals, or start a new one for this month.": "کسی بھی مہینے کے سودے دیکھنے کے لیے نیچے اس پر کلک کریں، یا اس مہینے کے لیے نیا سودہ شروع کریں۔",
  "+ Log a deal this month": "+ اس مہینے سودہ درج کریں",
  "No deals yet — log the first one to get started.": "ابھی کوئی سودہ نہیں — شروع کرنے کے لیے پہلا سودہ درج کریں۔",
  "{year} total": "{year} کا کل",

  "This month": "اس مہینے",

  // Table headers
  "Month": "مہینہ",
  "Deals": "سودے",
  "Sold": "فروخت شدہ",
  "Purchase": "خرید",
  "Sales": "فروخت",
  "Profit": "منافع",
  "Search deals…": "سودے تلاش کریں…",
  "Showing {from}–{to} of {total}": "{total} میں سے {from}–{to} دکھائے جا رہے ہیں",
  "Page {page} of {pages}": "صفحہ {page} از {pages}",
  "Date": "تاریخ",
  "Item Type": "آئٹم کی قسم",
  "Customer": "گاہک",
  "Supplier": "سپلائر",
  "Quantity": "مقدار",
  "Purchase Price": "خرید قیمت",
  "Sale Price": "فروخت قیمت",
  "Total Purchase Price": "کل خرید قیمت",
  "Total Sale Price": "کل فروخت قیمت",
  "Total Profit": "کل منافع",
  "Actions": "کارروائی",
  "Name": "نام",
  "Total": "کل",

  // Downloads
  "Excel": "ایکسل",
  "PDF": "پی ڈی ایف",
  "Download": "ڈاؤن لوڈ",
  "Download as Excel": "ایکسل ڈاؤن لوڈ کریں",
  "Download as PDF": "پی ڈی ایف ڈاؤن لوڈ کریں",
  "Start date": "شروع کی تاریخ",
  "End date": "آخری تاریخ",
  "Download deals": "سودے ڈاؤن لوڈ کریں",
  "Pick any date range — it can cover part of a year or several years.": "کوئی بھی تاریخی رینج منتخب کریں — یہ سال کا کچھ حصہ یا کئی سال پر محیط ہو سکتی ہے۔",
  "Defaults to your first and last deal — adjust to export any date range.": "پہلے سے آپ کے پہلے اور آخری سودے پر مقرر — کسی بھی تاریخ کی رینج ایکسپورٹ کرنے کے لیے تبدیل کریں۔",
  "End date can't be before the start date.": "آخری تاریخ شروع کی تاریخ سے پہلے نہیں ہو سکتی۔",

  // Month page / add-a-deal
  "{name} — deals": "{name} — سودے",
  "Back to {name}": "{name} پر واپس",
  "Add a deal": "سودہ شامل کریں",
  "Add deal": "سودہ شامل کریں",
  "One deal, multiple items — set the date and customer once below, then list each item with its own supplier.": "ایک سودہ، متعدد اشیاء — نیچے تاریخ اور گاہک ایک بار مقرر کریں، پھر ہر آئٹم اپنے سپلائر کے ساتھ درج کریں۔",
  "Item {n}": "آئٹم {n}",
  "+ Add item": "+ آئٹم شامل کریں",
  "Remove item": "آئٹم ہٹائیں",
  "Total profit": "کل منافع",
  "{n} items": "{n} اشیاء",
  "Deal total": "سودے کا کل",
  "Please correct the highlighted fields.": "براہ کرم نشان زد خانوں کو درست کریں۔",
  "Date is required": "تاریخ درکار ہے۔",
  "Customer is required": "گاہک درکار ہے۔",
  "Item type is required": "آئٹم کی قسم درکار ہے۔",
  "Supplier is required": "سپلائر درکار ہے۔",
  "Quantity must be greater than 0": "مقدار صفر سے زیادہ ہونی چاہیے۔",
  "Purchase price is required": "خرید قیمت درکار ہے۔",
  "Sale price is required": "فروخت قیمت درکار ہے۔",
  "Saving…": "محفوظ ہو رہا ہے…",
  "No deals in this month yet — add your first one above.": "اس مہینے ابھی کوئی سودہ نہیں — اوپر اپنا پہلا سودہ شامل کریں۔",

  // Row actions
  "Save": "محفوظ کریں",
  "Cancel": "منسوخ کریں",
  "Close": "بند کریں",
  "Edit": "ترمیم",
  "Delete this deal?": "کیا یہ سودہ حذف کریں؟",
  "Edit {name}": "{name} میں ترمیم",
  "Delete {name}": "{name} حذف کریں",

  // Lists detail
  "Add new": "نیا شامل کریں",
  "Add": "شامل کریں",
  "Add an item type…": "آئٹم کی قسم شامل کریں…",
  "Add a customer…": "گاہک شامل کریں…",
  "Add a supplier…": "سپلائر شامل کریں…",
  "Add…": "شامل کریں…",
  "Back to products": "پروڈکٹس پر واپس",
  "Search": "تلاش کریں",
  "None yet — add your first above.": "ابھی کوئی نہیں — اوپر اپنا پہلا شامل کریں۔",
  "No matches.": "کوئی نتیجہ نہیں۔",
  "No matches": "کوئی نتیجہ نہیں",
  "Remove “{name}”?": "کیا “{name}” ہٹا دیں؟",
  "Remove “{name}” from {title}?": "کیا “{name}” کو {title} سے ہٹا دیں؟",

  // Combobox
  "Select or add…": "منتخب کریں یا شامل کریں…",
  "Search or add…": "تلاش کریں یا شامل کریں…",
  "Clear selection": "انتخاب ہٹائیں",
  "+ Add “{q}”": "+ “{q}” شامل کریں",
  "Type to add the first one": "پہلا شامل کرنے کے لیے لکھیں",

  // Product modal
  "Update this product’s name.": "اس پروڈکٹ کا نام اپ ڈیٹ کریں۔",
  "Update this product's name.": "اس پروڈکٹ کا نام اپ ڈیٹ کریں۔",
  "Create a product to log its deals against.": "اس کے سودے درج کرنے کے لیے ایک پروڈکٹ بنائیں۔",
  "Create product": "پروڈکٹ بنائیں",
  "Product name": "پروڈکٹ کا نام",
  "Product Type": "پروڈکٹ کی قسم",
  "Add a product type…": "پروڈکٹ کی قسم شامل کریں…",
  "Only item types saved under the same product type will show up when logging a deal.": "صرف وہی آئٹم اقسام دکھائی دیں گی جو اسی پروڈکٹ کی قسم کے تحت محفوظ ہیں جب سودہ درج کیا جائے گا۔",

  // Date picker
  "Select date": "تاریخ منتخب کریں",
  "Previous": "پچھلا",
  "Next": "اگلا",
  "Clear": "صاف کریں",
  "Today": "آج",

  // Months (both long and short grids show full Urdu names — they fit the cells)
  "January": "جنوری", "February": "فروری", "March": "مارچ", "April": "اپریل",
  "May": "مئی", "June": "جون", "July": "جولائی", "August": "اگست",
  "September": "ستمبر", "October": "اکتوبر", "November": "نومبر", "December": "دسمبر",
  "Jan": "جنوری", "Feb": "فروری", "Mar": "مارچ", "Apr": "اپریل",
  "Jun": "جون", "Jul": "جولائی", "Aug": "اگست",
  "Sep": "ستمبر", "Oct": "اکتوبر", "Nov": "نومبر", "Dec": "دسمبر",
};

const DICT = { ur: UR };

function initialLang() {
  if (typeof window === "undefined") return "en";
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "ur" ? "ur" : "en";
  } catch {
    return "en";
  }
}

function interpolate(str, vars) {
  let out = str;
  for (const k in vars) out = out.split(`{${k}}`).join(String(vars[k]));
  return out;
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(initialLang);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((l) => {
    const next = l === "ur" ? "ur" : "en";
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore private-mode storage errors */
    }
  }, []);

  const t = useCallback(
    (s, vars) => {
      const table = DICT[lang];
      const translated = table && table[s] != null ? table[s] : s;
      return vars ? interpolate(translated, vars) : translated;
    },
    [lang],
  );

  const value = {
    lang,
    setLang,
    toggle: () => setLang(lang === "ur" ? "en" : "ur"),
    t,
  };
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

// Safe even if a component renders outside the provider (identity translation).
export function useI18n() {
  return (
    useContext(I18nContext) || {
      lang: "en",
      setLang: () => {},
      toggle: () => {},
      t: (s, vars) => (vars ? interpolate(s, vars) : s),
    }
  );
}

// "2026-07" -> "July 2026" / "جولائی 2026" (month name localized, year as-is).
export function monthLabel(ym, t) {
  if (!ym || typeof ym !== "string") return ym || "";
  const [y, m] = ym.split("-").map(Number);
  const name = MONTHS_LONG[(m || 1) - 1] || "";
  return `${t ? t(name) : name} ${y}`;
}

// Compact EN | اردو switch for the top bar.
export function LanguageToggle({ className = "" }) {
  const { lang, setLang } = useI18n();
  const btn = "h-8 rounded-md px-2.5 text-xs font-semibold transition";
  return (
    <div className={`flex shrink-0 items-center rounded-lg border border-slate-200 bg-slate-50 p-0.5 ${className}`}
      role="group" aria-label="Language">
      <button type="button" onClick={() => setLang("en")} aria-pressed={lang === "en"}
        className={`${btn} ${lang === "en" ? "bg-white text-teal-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
        EN
      </button>
      <button type="button" onClick={() => setLang("ur")} aria-pressed={lang === "ur"}
        className={`${btn} ${lang === "ur" ? "bg-white text-teal-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
        اردو
      </button>
    </div>
  );
}
